import { twMerge } from "tailwind-merge";
import { CommandJob } from "~/components/build/Show/lib/types";
import PipelineStateIcon, {
  colorForJob,
} from "~/components/shared/PipelineStateIcon/PipelineStateIcon";
import { JobRetries } from "~/components/build/Show/routes/steps/list/-components/TreeNode/JobRetries";
import { QueueLabel } from "~/components/build/Show/routes/steps/list/-components/TreeNode/QueueLabel";
import { useBuild, useRetriesForJob } from "~/components/build/Show/lib/BuildContext";
import { getJobTimingCell } from "~/components/build/Show/routes/steps/list/-components/timingColumns";
import { CommandStep, isDefaultCommandStep, isParallelCommandStep } from "~/lib/pipeline";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import { ParallelJobIndex } from "~/components/build/Show/components/ParallelJobIndex";
import { ParallelCommandJob } from "~/components/build/Show/lib/types/CommandJob";
import Emojify from "~/components/shared/Emojify";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { StepRow } from "~/components/build/Show/routes/steps/list/-components/StepRow";

export const JobLabel = ({
  job,
  step,
  depth = 0,
}: {
  job: CommandJob;
  step: CommandStep;
  depth?: number;
}) => {
  const { build } = useBuild();
  const retries = useRetriesForJob(job, { includeLatest: false });
  const { missingDependencies } = useStepDependencies(step.uuid);

  const waitingOnConcurrencyGroup = job.state === "limited";
  const waitingOnPlatformLimit = job.state === "platform_limited";
  const name = job.name || job.command || "";
  const timing = getJobTimingCell(job);

  return (
    <StepRow
      depth={depth}
      stepType={step.type}
      icon={
        isDefaultCommandStep(step) ? (
          <StateIcon
            {...step}
            waitingOnConcurrencyGroup={waitingOnConcurrencyGroup}
            waitingOnPlatformLimit={waitingOnPlatformLimit}
            missingDependencies={missingDependencies}
            className={twMerge(
              textColorForStep({
                ...step,
                waitingOnConcurrencyGroup,
                waitingOnPlatformLimit,
                missingDependencies,
              }),
            )}
          />
        ) : (
          <PipelineStateIcon
            job={job}
            className={twMerge("w-4", colorForJob(job, build))}
            style={{ width: undefined, height: undefined }}
          />
        )
      }
      label={
        <span className="inline space-x-1">
          {isParallelCommandStep(step) && <ParallelJobIndex job={job as ParallelCommandJob} />}
          <Emojify text={name} />
        </span>
      }
      script={job.command || undefined}
      stepStats={retries.length > 0 ? <JobRetries job={job} /> : undefined}
      meta={
        job.clusterQueue ? (
          <QueueLabel
            queueKey={job.clusterQueue.key}
            os={job.instanceShape?.operatingSystem}
            job={job}
          />
        ) : undefined
      }
      timing={timing}
    />
  );
};
